import { createContext, useContext, useState, useEffect } from "react";
import { fetchCartItems, addCartItem, deleteCartItem } from "../common/api";
import { useAuth } from "./AuthContext";

const BASE_URL = process.env.REACT_APP_API_URL;
const CartContext = createContext();

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setCartItems([]);
      setCartError(null);
      return;
    }

    setCartLoading(true);
    setCartError(null);
    fetchCartItems()
      .then((items) => setCartItems(items.map((i) => ({ ...i, id: i.id }))))
      .catch((err) => {
        console.error(err);
        setCartError("We couldn't load your cart. Please try again.");
      })
      .finally(() => setCartLoading(false));
  }, [user]);

  const addToCart = async (item) => {
    setCartError(null);
    try {
      const { id } = await addCartItem({
        tour_id: item.tourId,
        title: item.title,
        date: item.date,
        persons: item.persons,
        per_person: item.perPerson,
        total: item.total,
        image_url: item.imgSrc,
      });
      setCartItems((prev) => [...prev, { ...item, id }]);
      return id;
    } catch (err) {
      console.error("Failed to add to cart:", err);
      setCartError("We couldn't add this tour to your cart.");
      throw err;
    }
  };

  const removeFromCart = async (id) => {
    setCartError(null);
    try {
      await deleteCartItem(id);
      setCartItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Failed to remove from cart:", err);
      setCartError("We couldn't remove this item. Please try again.");
      throw err;
    }
  };

  const clearCart = async () => {
    setCartError(null);
    try {
      const res = await fetch(`${BASE_URL}/cart/clear`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!res.ok) throw new Error("Failed to clear cart");
      setCartItems([]);
    } catch (err) {
      console.error("Failed to clear cart:", err);
      setCartError("We couldn't clear your cart. Please try again.");
      throw err;
    }
  };

  const clearCartError = () => setCartError(null);

  return (
    <CartContext.Provider value={{ cartItems, cartLoading, cartError, clearCartError, addToCart, removeFromCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
